import {_DEFAULT_NUTRITIONAL_PROPERTIES, IFoodGroupIngredient, INutritionalProperties} from '../../models/food-group';
import {cloneDeep, flattenDeep, groupBy, sumBy} from 'lodash';
import {PlanPeriodClass} from './plan-period.class';
import {MealPlanRequestDTO} from '../../dtos/meal-plan/meal-plan-request.dto';
import {PlanPeriodDTO} from '../../dtos/meal-plan/plan-period.dto';
import {IBlockPeriod, IIngredientShoppingKart, IPlanPeriod} from '../../models/meal-plans';
import {BlockPeriodClass} from './block-period.class';
import {AnyObject} from '../../interfaces/utils';
import {MacronutrientDistribution} from '../macronutrient-distribution-strategy/macronutrient-distribution';
import {MacronutrientDistributionTypeEnum} from '../macronutrient-distribution-strategy/enums/macronutrient-distribution-type.enum';


export class MealPlanClass {
  // tslint:disable:variable-name
  _id?: string;
  instructions: string;
  name: string;
  tags: any[];
  nutritionalProperties: INutritionalProperties;
  planPeriods: PlanPeriodClass[];
  change?: boolean;
  hiddenOnList: boolean;
  modeRealOnly?: boolean;
  metadata?: AnyObject;
  official?: boolean;
  hasAnyAllergicIngredient?: boolean;
  hasAnyDislikeIngredient?: boolean;
  constructor(
    id?: string,
    name?: string,
    instructions?: string,
    tags?: string[],
    nutritionalProperties?: INutritionalProperties,
    planPeriods?: PlanPeriodClass[],
    metadata?: AnyObject,
    isOfficial?: boolean
  ) {
    this._id = id ?? null;
    this.name = name ?? '';
    this.instructions = instructions ?? '';
    this.tags = tags ?? [];
    this.nutritionalProperties = nutritionalProperties ?? cloneDeep(_DEFAULT_NUTRITIONAL_PROPERTIES);
    this.planPeriods = planPeriods ?? [];
    this.change = false;
    this.modeRealOnly = false;
    this.metadata = metadata;
    this.official = isOfficial || false;
  }

  setName(name: string): void {
    this.name = name;
  }

  setInstructions(instructions: string): void {
    this.instructions = instructions;
  }

  setHiddenOnList(value: boolean): void {
    this.hiddenOnList = value;
  }

  setTags(value: string[]): void {
    this.tags = value;
  }

  setChange(value: boolean): void {
    this.change = value;
  }

  setHasAnyAllergicIngredient(value: boolean): void {
    this.hasAnyAllergicIngredient = value;
  }

  setHasAnyDislikeIngredient(value: boolean): void {
    this.hasAnyDislikeIngredient = value;
  }

  createPlanPeriod(name?: string): void {
    const planPeriod: PlanPeriodClass = new PlanPeriodClass(name ? {name: name} : null, [], []);
    this.planPeriods.push(planPeriod);
  }

  deletePlanPeriod(index: number): void {
    this.planPeriods.splice(index, 1);
  }

  setModeReadOnly(value: boolean): void {
    this.modeRealOnly = value;
    this.planPeriods.forEach(period => period.setModeReadOnly(value));
  }

  syncNutritionalProperties(): void {
    this.planPeriods.forEach(period =>  period.mealTimes.forEach(mealTime => mealTime.syncNutritionalProperties()));
  }

  getRaw(): MealPlanRequestDTO {
    return {
      name: this.name || '',
      instructions: this.instructions || '',
      hiddenOnList: this.hiddenOnList || false,
      tags: this.tags || [],
      planPeriods: this.getRawValuePlanPeriods(),
      metadata: this.metadata || undefined
    };
  }


  private getRawValuePlanPeriods(): PlanPeriodDTO[] {
    const planPeriods: PlanPeriodDTO[] = [];
    this.planPeriods.forEach(planPeriod => {
      planPeriods.push({
        name: planPeriod.name,
        metadata: planPeriod.metadata,
        blockPeriods: planPeriod.getRawValueBlockPeriods(),
        mealTimes: planPeriod.getRawValueMealTimes()
      });
    });
    return planPeriods;
  }

  autoSaveBlocksInEdition(): void {
    this.planPeriods.forEach(period => {
      period.setAddNewBlock(false);
      period.blockPeriods.forEach((block, index) => {
        block.setEditMode(false);
        if (!block.isValid()) {
          period.deleteBlockPeriod(index);
        } else {
          // Marcar los bloques como validos como edición
          block.setIsNewBlock(false);
        }
      });
    });
  }

  blocksAreValid(): void {
    this.planPeriods.forEach((period: PlanPeriodClass) => {
      period.blockPeriods.forEach((block: BlockPeriodClass) => {
        block.isValid();
      });
      period.valid = period.blockPeriods.filter(({valid}: BlockPeriodClass) => valid === true).length === period.blockPeriods.length;
    });
  }

  blocksWithoutType(): boolean {
    let count = 0;
    this.planPeriods.forEach(period => {
      period.blockPeriods.forEach((block, index) => {
        if (!block.hasType()) {
          count += 1;
          block.setEditMode(true);
        }
      });
    });
    return (count > 0);
  }

  calculateShoppingKartByPeriod(periodIndex: number): any {
    const period = this.planPeriods[periodIndex];

    // conseguir los ingredientes de todos los block periods
    return period.blockPeriods
      .map(blockPeriod => blockPeriod.ingredients)
      .reduce((prev, current) => prev.concat(current), [])
      .reduce((prev: IIngredientShoppingKart, current) => {
        const obj: IIngredientShoppingKart = {
          ...prev
        };
        if (prev[current.ingredientId]) {
          obj[current.ingredientId] = {
            ...obj[current.ingredientId],
            occurrences: prev[current.ingredientId].occurrences.concat([current]),
            grams: 0
          };
          const index = prev[current.ingredientId].measures
            .findIndex(measure => measure.name === current.measure?.namePlural);
          if (index !== -1) {
            obj[current.ingredientId].measures[index] = {
              ...obj[current.ingredientId].measures[index],
              value: obj[current.ingredientId].measures[index].value + current.units
            };
          } else {
            obj[current.ingredientId].measures.push({
              symbol: current.measure.symbol,
              name: current.measure?.namePlural,
              value: current.units
            });
          }
        } else {
          obj[current.ingredientId] = {
            name: current.ingredientName,
            occurrences: [current],
            grams: 0,
            measures: [
              {
                symbol: current.measure.symbol ?? current.measure.symbol_es,
                name: current.measure?.namePlural ?? current.measure?.namePlural_es,
                value: current.units
              }
            ]
          };
        }
        return obj;
      }, {});
  }

  groupByCategory(): { [key: string]: number } {
    const ingredients: Partial<IFoodGroupIngredient>[][][] =
      this.planPeriods.map((period: PlanPeriodClass) => period.blockPeriods.map((block: BlockPeriodClass) => block.ingredients));
    const groups = groupBy(flattenDeep(ingredients, 4), 'category._id');
    for (const prop in groups) {
      if (groups.hasOwnProperty(prop)) {
        groups[prop] = {
          name: groups[prop][0]?.category.name,
          quantity: groups[prop].length,
          smaePortion: sumBy(groups[prop], el => el.smae?.portion)
        };
      }
    }
    return groups;
  }

  setMacronutrientDistribution(value: MacronutrientDistribution, strategyName: MacronutrientDistributionTypeEnum): void {
    try {
      const macros: string[] = Object.keys(value);
      const metadataDistribution: {} = {};
      macros.forEach((macro: string) => {
        metadataDistribution[macro] = {
          block: value[macro].block,
          value: value[macro].value,
          defaultValue: value[macro].defaultValue, // TODO: Agregue la propiedad defaultValue
        };
      });
      const distribution = {
        ...this.metadata.distribution,
        [strategyName]: metadataDistribution,
        current: strategyName
      };
      this.metadata = {
        ...this.metadata,
        distribution: cloneDeep(distribution)
      };
      this.planPeriods.forEach((period: PlanPeriodClass) => {
        period.metadata = {
          ...period.metadata,
          distribution: cloneDeep(distribution)
        };
      });
    } catch (e) {
      console.log(e);
    }
  }

  checkIfMealPlanHasAnyAllergicIngredient(): void {
    if (!this.planPeriods) return;
    const hasAnyAllergicIngredient: boolean =
      this.planPeriods.some((period: IPlanPeriod) =>
        period.blockPeriods.some((block: IBlockPeriod) =>
          block.ingredients.some((ingredient: IFoodGroupIngredient) => ingredient.isAllergic)));
    this.setHasAnyAllergicIngredient(hasAnyAllergicIngredient);
  }

  checkIfMealPlanHasAnyDislikeIngredient(): void {
    if (!this.planPeriods) return;
    const hasAnyDislikeIngredient: boolean =
      this.planPeriods.some((period: IPlanPeriod) =>
        period.blockPeriods.some((block: IBlockPeriod) =>
          block.ingredients.some((ingredient: IFoodGroupIngredient) => ingredient.isDislike)));
    this.setHasAnyDislikeIngredient(hasAnyDislikeIngredient);
  }
}
